/* eslint-disable @typescript-eslint/no-explicit-any */
import { ThProps } from "@/InterfaceProps";
import cx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  ActionIcon,
  Anchor,
  Center,
  Group,
  ScrollArea,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  rem,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconPencil,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";
import ConfirmDeleteCaseModal from "../ConfirmDeleteCaseModal";
import EditCaseModal from "../EditCaseModal";
import classes from "./CaseTable.module.css";

interface RowData {
  title: string;
  description: string;
  updatedAt: string;
  createdAt: string;
  templates: any;
}

const CaseTable = ({
  data,
  loadMoreCases,
  isLoading,
  fetchCases,
  search,
  patientId,
  patient,
  templates,
}: any) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [patientCase, setPatientCase] = useState<any>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<any>(null);
  const [scrollPosition, onScrollPositionChange] = useState({ x: 0, y: 0 });
  useEffect(() => {
    setScrolled(scrollPosition.y != 0);
  }, [scrollPosition]);

  const [sortedData, setSortedData] = useState<any>([]);
  useEffect(() => {
    setSortedData(data);
  }, [data]);

  useEffect(() => {
    setSortedData(
      sortData(data, { sortBy, reversed: !reverseSortDirection, search }),
    );
  }, [search]);

  const filterData = (data: RowData[], search: string) => {
    const query = search?.toLowerCase()?.trim();
    const keysFilter: any = ["title", "description"];
    return data?.filter((item: any) =>
      keysFilter.some((key: any) => item[key]?.toLowerCase().includes(query)),
    );
  };

  const rows = sortedData.map((row: any) => {
    const strCreated = moment(row.createdAt).format("MM/DD/YYYY, hh:mm A");
    const strUpdated = moment(row.updatedAt).format("MM/DD/YYYY, hh:mm A");

    return (
      <Table.Tr key={row.id} style={{ cursor: "auto" }}>
        <Table.Td>
          <Anchor
            onClick={() =>
              navigate(`/case-detail/${row.id}`, {
                state: { ...row, patientId, patient },
              })
            }
          >
            <Group gap={5}>{row.title}</Group>
          </Anchor>
        </Table.Td>
        <Table.Td>{row.description}</Table.Td>
        <Table.Td miw={170}>{strCreated}</Table.Td>
        <Table.Td miw={170}>{strUpdated}</Table.Td>
        <Table.Td className="text-center">
          <Tooltip label={"Edit case"} withArrow>
            <ActionIcon
              size="sm"
              mx={3}
              variant="subtle"
              radius={20}
              color="gray"
              onClick={() => {
                setPatientCase(row);
                setModalOpened(true);
              }}
            >
              <IconPencil size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={"Delete case"} withArrow>
            <ActionIcon
              mx={3}
              color="red"
              size="sm"
              radius={20}
              variant="subtle"
              onClick={() => {
                setPatientCase(row);
                setModalConfirmDeleteOpened(true);
              }}
            >
              <IconTrash size={20} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      </Table.Tr>
    );
  });

  const Th = ({
    children,
    reversed,
    sorted,
    onSort,
    index,
    style,
    id,
  }: ThProps) => {
    const Icon = sorted
      ? reversed
        ? IconChevronUp
        : IconChevronDown
      : IconSelector;
    return (
      <Table.Th id={id} className={classes.th} style={style}>
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
        <div
          className="resize-handle"
          onMouseDown={(e) => handleMouseDown(e, index)}
        ></div>
      </Table.Th>
    );
  };
  const handleScroll = (event: any) => {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    if (scrollHeight - scrollTop === clientHeight && !isLoading) {
      loadMoreCases();
    }
  };
  const setSorting = (field: keyof RowData) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(data, { sortBy: field, reversed, search }));
  };
  function sortData(
    data: RowData[],
    payload: {
      sortBy: keyof RowData | null;
      reversed: boolean;
      search: string;
    },
  ) {
    const { sortBy } = payload;
    if (!sortBy) {
      return filterData(data, payload.search);
    }
    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return b[sortBy]?.localeCompare(a[sortBy]);
        }

        return a[sortBy]?.localeCompare(b[sortBy]);
      }),
      payload.search,
    );
  }
  const [columns, setColumns] = useState<any>([
    { id: 1, name: "Title", sortBy: "title" },
    { id: 2, name: "Description", sortBy: "description" },
    { id: 3, name: "Created", sortBy: "createdAt" },
    { id: 4, name: "Updated", sortBy: "updatedAt" },
    { id: 5, name: "Actions" },
  ]);
  const [resizing, setResizing] = useState({
    isResizing: false,
    columnIndex: -1,
    startX: 0,
    startWidth: 0,
  });

  const handleMouseDown = (e: any, index: number) => {
    const colEl = document.getElementById(`col-${columns[index].id}`);
    setResizing({
      isResizing: true,
      columnIndex: index,
      startX: e.clientX,
      startWidth: colEl?.clientWidth as number,
    });
  };

  const handleMouseMove = (e: any) => {
    if (!resizing.isResizing) return;

    let newWidth = resizing.startWidth + (e.clientX - resizing.startX);
    if (newWidth < 50) {
      newWidth = 50;
    }
    setColumns((prevColumns: any) =>
      prevColumns.map((col: any, index: number) =>
        index === resizing.columnIndex ? { ...col, width: newWidth } : col,
      ),
    );
  };

  const handleMouseUp = () => {
    try {
      if (resizing.isResizing) {
        setResizing({ ...resizing, isResizing: false });
      }
    } catch (error) {
      console.log(error);
    }
  };
  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  return (
    <>
      <ScrollArea
        h={700}
        w={"100%"}
        scrollbars="xy"
        ref={scrollAreaRef}
        onTouchMove={handleScroll}
        onWheel={handleScroll}
        onScrollPositionChange={onScrollPositionChange}
        viewportRef={viewport}
      >
        <div onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
          <Table className="vetscribe-table">
            <Table.Thead
              className={cx(classes.header, { [classes.scrolled]: scrolled })}
            >
              <Table.Tr>
                {columns.map((column: any, index: number) => {
                  if (column.sortBy) {
                    return (
                      <Th
                        key={index}
                        sorted={sortBy === column.sortBy}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting(column.sortBy as any)}
                        index={index}
                        style={{ width: column?.width }}
                        id={`col-${column.id}`}
                      >
                        {column.name}
                      </Th>
                    );
                  }
                  return (
                    <Table.Th
                      key={index}
                      className="text-center th-action"
                      fw={500}
                      fz={"sm"}
                      ta={"center"}
                    >
                      <UnstyledButton className="control">
                        <Group justify="center">{column.name}</Group>
                      </UnstyledButton>
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
      </ScrollArea>
      <EditCaseModal
        modalOpened={modalOpened}
        setModalOpened={setModalOpened}
        patientCase={patientCase}
        fetchCases={fetchCases}
        setPatientCase={setPatientCase}
        setModalConfirmDeleteOpened={setModalConfirmDeleteOpened}
        templates={templates}
      />
      <ConfirmDeleteCaseModal
        modalOpened={modalConfirmDeleteOpened}
        setModalOpened={setModalConfirmDeleteOpened}
        patientCase={patientCase}
        fetchCases={fetchCases}
      />
    </>
  );
};

export default CaseTable;
